// A purchase order: the numbered record of one purchase from one vendor.
// Answers: what did we order, from whom, what state is it in, and what did it really cost?
//
// The state is never typed: it follows from placing the order, from what has been received
// against its lines, and from closing it. Actual unit prices, shipping and tax are fields on
// this record; they can be typed here, and receipt matching will fill the same fields.

import { useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { lineCents, shippingEstimate, type ShippingRule } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { deleteRow, insertRow, toast, updateRow, useRows, type Row } from '../../lib/api';
import { longDate, money, qty, shortDate, toCents, today } from '../../lib/format';
import { closeShort, deleteDraftOrder, deleteReceipt, receive, receiveAll, refreshInventory, reopenShort, takeOffOrder, undoReceipt, useInventory, useVendors, type OrderLineView, type OrderView } from '../../lib/inventory';
import { jobTitle } from '../../lib/jobs';
import { useSession } from '../../lib/session';
import { Btn, Edit, Empty, Field, Info, Loading, Modal, Money, Panel, Tag } from '../../ui/kit';
import { uploadFile, useSignedUrl } from '../job/Files';
import { useInventoryNames, useOrderStateNames } from './names';

const amount = (t: string) => (t.trim() === '' ? '' : /^\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined);
const positive = (t: string) => (/^\d*\.?\d+$/.test(t.trim()) && Number(t) > 0 ? t.trim() : undefined);

async function save(table: string, id: string, patch: Row): Promise<void> {
  await updateRow(table, id, patch);
  refreshInventory();
}

export function PurchaseOrder({ orderId, onClose, openMaterial }: { orderId: string; onClose: () => void; openMaterial: (id: string) => void }) {
  const s = useSession();
  const inv = useInventory();
  const vendors = useVendors();
  const stateNames = useOrderStateNames();
  const o = inv.orders.find((x) => x.po.id === orderId);
  const canBuy = s.canEdit('screen.purchasing');

  if (inv.loading || !o) {
    return (
      <Modal title="Purchase order" onClose={onClose}>
        {inv.loading ? <Loading /> : <Empty>This order is no longer there.</Empty>}
      </Modal>
    );
  }
  const { po, vendor, state } = o;
  const draft = state === 'draft';
  const receipt = po.kind === 'receipt';
  const jobName = (id: string | null) => {
    const j = inv.jobs.find((x) => x.job.id === id);
    return j ? jobTitle(j) : null;
  };
  const copyNumber = () => void navigator.clipboard.writeText(String(po.number)).then(() => toast(`${po.number} copied.`));

  return (
    <Modal
      wide
      onClose={onClose}
      title={
        <>
          {receipt ? 'Receipt' : po.number} · {vendor?.name ?? 'no vendor'} <Tag kind={draft ? 'un' : state === 'closed' ? 'off' : 'on'}>{stateNames[state]}</Tag>
        </>
      }
    >
      <div className="stack">
        <div className="row mid" style={{ justifyContent: 'space-between' }}>
          <span className="row mid" style={{ gap: 8 }}>
            {!receipt && (
              <Btn small read onClick={copyNumber}>
                Copy {po.number}
              </Btn>
            )}
            {!receipt && (
              <Link className="btn sm" to={`/inventory/orders/${po.id}`}>
                PO document
              </Link>
            )}
            {vendor?.url && (
              <a className="btn sm" href={String(vendor.url)} target="_blank" rel="noreferrer">
                {vendor.name}'s site
              </a>
            )}
            <small>
              {receipt
                ? 'Bought in person: stock in, with no order behind it.'
                : vendor?.order_method === 'web'
                  ? `Key ${po.number} into the cart as the PO or reference number.`
                  : vendor?.order_method === 'document'
                    ? 'Send the PO document to the vendor.'
                    : 'How this vendor takes orders (a web cart, or a document to send) is not set yet: Library → Vendors.'}
            </small>
          </span>
          {(canBuy || s.can('screen.receiving')) && (
            <span className="row mid" style={{ gap: 8 }}>
              {receipt && canBuy && state !== 'closed' && (
                <Btn
                  small
                  title="Entered by mistake: remove the receipt and take its stock back."
                  onClick={async () => {
                    if (!window.confirm('Delete this receipt? The stock it brought in is taken back out.')) return;
                    await deleteReceipt(o);
                    onClose();
                  }}
                >
                  Delete receipt
                </Btn>
              )}
              {draft && canBuy && (
                <>
                  <Btn
                    small
                    onClick={async () => {
                      if (!window.confirm(`Delete the draft ${po.number}? Its lines go back to the buy list.`)) return;
                      await deleteDraftOrder(po.id);
                      onClose();
                    }}
                  >
                    Delete draft
                  </Btn>
                  <Btn primary disabled={o.lines.length === 0} onClick={() => void save('purchase_order', po.id, { placed_at: new Date().toISOString() }).then(() => toast(`${po.number} placed.`))}>
                    Place order
                  </Btn>
                </>
              )}
              {state === 'placed' && canBuy && !receipt && (
                <Btn small title="Nothing has been received, so it can go back to a draft." onClick={() => void save('purchase_order', po.id, { placed_at: null })}>
                  Back to draft
                </Btn>
              )}
              {(state === 'placed' || state === 'partly_received') && !receipt && s.can('screen.receiving') && (
                <Btn primary onClick={() => void receiveAll(o, s.user?.id ?? null).then(() => toast('Everything outstanding is in.'))}>
                  Receive everything
                </Btn>
              )}
              {state === 'received' && canBuy && (
                <Btn primary={!receipt} small={receipt} title="Nothing more is coming and the prices are in: file it." onClick={() => void save('purchase_order', po.id, { closed_at: new Date().toISOString() })}>
                  {receipt ? 'Close' : 'Close order'}
                </Btn>
              )}
              {state === 'closed' && canBuy && (
                <Btn small onClick={() => void save('purchase_order', po.id, { closed_at: null })}>
                  Reopen
                </Btn>
              )}
            </span>
          )}
        </div>

        <div className="form">
          <Field label="Vendor">
            {draft && canBuy ? (
              <select value={po.vendor_id ?? ''} onChange={(e) => e.target.value && void save('purchase_order', po.id, { vendor_id: e.target.value })}>
                {vendors.rows.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            ) : (
              <input type="text" disabled value={vendor?.name ?? 'none'} />
            )}
          </Field>
          <Field label={draft ? 'Started' : 'Placed'}>
            <input type="text" disabled value={longDate(po.placed_at ?? po.created_at)} />
          </Field>
          <Field label="Note" wide>
            <Edit disabled={!canBuy} value={po.note ?? ''} placeholder="anything the vendor or the shop should know" onSave={(v) => save('purchase_order', po.id, { note: v || null })} />
          </Field>
        </div>

        <Lines o={o} canBuy={canBuy} jobName={jobName} openMaterial={openMaterial} />
        <div className="row">
          <div className="col">
            <Totals o={o} />
          </div>
          <div className="col">
            <Invoices o={o} canBuy={canBuy} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Lines({ o, canBuy, jobName, openMaterial }: { o: OrderView; canBuy: boolean; jobName: (id: string | null) => string | null; openMaterial: (id: string) => void }) {
  const s = useSession();
  const names = useInventoryNames();
  const draft = o.state === 'draft';
  const closed = o.state === 'closed';
  const prices = s.can('see_prices');
  const canReceive = s.can('screen.receiving');
  if (o.lines.length === 0) return <Empty>No lines on this order. Lines are added from the buy list, or from a material in {names.supply_demand}.</Empty>;
  return (
    <div className="tblwrap">
      <table>
        <thead>
          <tr>
            <th>Material</th>
            <th>For</th>
            <th className="right">Qty</th>
            <th>Unit</th>
            <th className="right">Est. price</th>
            <th className="right">Est. total</th>
            <th>{draft ? 'Lead time' : 'Expected'}</th>
            {!draft && <th className="right">Received</th>}
            {!draft && prices && <th className="right">Actual price</th>}
            {!draft && prices && <th className="right">Actual total</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {o.lines.map((l) => (
            <tr key={l.row.id}>
              <td>
                <button type="button" className="linklike read" onClick={() => openMaterial(l.material.id)}>
                  {l.material.description}
                </button>
                {l.material.part_number && <small> · {l.material.part_number}</small>}
              </td>
              <td>
                <For l={l} jobName={jobName} />
              </td>
              <td className="right num">
                {draft && canBuy ? <Edit className="cell short right" value={qty(l.row.qty)} parse={positive} onSave={(v) => save('purchase_order_line', l.row.id, { qty: Number(v) })} /> : qty(l.row.qty)}
                {Number(l.row.closed_short_qty) > 0 && <small> ({qty(l.row.closed_short_qty)} closed short)</small>}
              </td>
              <td>{l.material.unit ?? '—'}</td>
              <td className="right num">{prices && l.row.est_unit_price !== null ? money(toCents(l.row.est_unit_price)) : '—'}</td>
              <td className="right">
                <Money cents={l.row.est_unit_price === null ? null : l.est_cents} />
              </td>
              <td className="nowrap">
                {draft ? (
                  l.lead_days === null ? <small>not known</small> : `${l.lead_days} days`
                ) : o.po.kind === 'receipt' ? (
                  <small>in hand</small>
                ) : (
                  <>
                    {l.expected_on ? shortDate(l.expected_on) : <small>lead time not known</small>}
                    {canBuy && Number(l.outstanding) > 0 && (
                      <>
                        {' '}
                        <Edit type="date" className="cell" value={l.row.expected_on ?? ''} onSave={(v) => save('purchase_order_line', l.row.id, { expected_on: v || null })} />
                      </>
                    )}
                  </>
                )}
              </td>
              {!draft && (
                <td className="right num">
                  <span className="nowrap">
                    {qty(l.received)} of {qty(Number(l.row.qty) - Number(l.row.closed_short_qty))} {Number(l.outstanding) > 0 && Number(l.received) > 0 && <Tag kind="un">{qty(l.outstanding)} backordered</Tag>}
                  </span>
                  {l.receipts.map((r) => (
                    <div key={r.id} className="nowrap">
                      <small>
                        {shortDate(r.created_at)} · {qty(r.qty)}{' '}
                        {canReceive && !closed && o.po.kind === 'order' && (
                          <button type="button" className="linklike" title="Entered by mistake: take this receipt, and its stock, back." onClick={() => void undoReceipt(r.id)}>
                            undo
                          </button>
                        )}
                      </small>
                    </div>
                  ))}
                </td>
              )}
              {!draft && prices && (
                <td className="right num">
                  <Edit className="cell short right" disabled={!canBuy} value={l.row.actual_unit_price === null ? '' : Number(l.row.actual_unit_price)} placeholder="from the invoice" parse={amount} onSave={(v) => save('purchase_order_line', l.row.id, { actual_unit_price: v === '' ? null : Number(v) })} />
                </td>
              )}
              {!draft && prices && (
                <td className="right">
                  <Money cents={l.actual_cents} />
                </td>
              )}
              <td className="nowrap">
                {draft && canBuy && (
                  <button type="button" className="linklike" title="Take this line off the order. It goes back to the buy list." onClick={() => void takeOffOrder(l.row.id)}>
                    Take off
                  </button>
                )}
                {!draft && !closed && o.po.kind === 'order' && Number(l.outstanding) > 0 && (
                  <>
                    {canReceive && <ReceiveCell line={l} />}{' '}
                    {canBuy && (
                      <button
                        type="button"
                        className="linklike"
                        title="The rest is not coming. Stop waiting for it."
                        onClick={() => window.confirm(`Close the remaining ${qty(l.outstanding)} ${l.material.unit ?? ''} of ${l.material.description} short? It will no longer be expected, and a job that was counting on it will show it to buy again.`) && void closeShort(l)}
                      >
                        Close short
                      </button>
                    )}
                  </>
                )}
                {!draft && !closed && canBuy && Number(l.row.closed_short_qty) > 0 && (
                  <>
                    {' '}
                    <button type="button" className="linklike" title="It is coming after all: expect it again." onClick={() => void reopenShort(l)}>
                      Expect it again
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** How much of a line arrived: the whole outstanding quantity unless a smaller number is typed. */
function ReceiveCell({ line }: { line: OrderLineView }) {
  const s = useSession();
  const [amount, setAmount] = useState('');
  const n = amount.trim() === '' ? Number(line.outstanding) : Number(amount);
  return (
    <span className="nowrap">
      <input type="number" className="short" style={{ width: 76 }} min="0" step="any" placeholder={qty(line.outstanding)} value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Quantity received" />{' '}
      <Btn
        small
        primary
        disabled={!(n > 0)}
        onClick={async () => {
          await receive(line, n, s.user?.id ?? null);
          setAmount('');
          toast(n < Number(line.outstanding) ? `${qty(n)} in. ${qty(Number(line.outstanding) - n)} still to come (backordered).` : `${qty(n)} in.`);
        }}
      >
        Receive
      </Btn>
    </span>
  );
}

/** Who the line is for: each job's share, and what is left over for the shelf. */
/** The vendor pages a request carries in its note ("L-07: https://…"), one per line, with the item that typed each. */
export function requestLinks(requests: Row[]): { number: string; url: string }[] {
  const out: { number: string; url: string }[] = [];
  for (const r of requests) {
    for (const line of String(r.note ?? '').split('\n')) {
      const m = /^(.*?):\s*(https?:\/\/\S+)\s*$/.exec(line.trim());
      if (m && !out.some((x) => x.number === m[1] && x.url === m[2])) out.push({ number: m[1]!.trim(), url: m[2]! });
    }
  }
  return out;
}

function For({ l, jobName }: { l: OrderLineView; jobName: (id: string | null) => string | null }) {
  const parts = l.requests.map((r) => ({ id: r.id as string, who: r.job_id ? jobName(r.job_id) ?? 'a job' : 'stock', amount: l.share[r.id]?.qty ?? '0', early: r.kind === 'early' }));
  const links = requestLinks(l.requests);
  return (
    <small>
      {parts.map((p) => (
        <span key={p.id}>
          {p.who} · {qty(p.amount)} {p.early && <Tag kind="un">pulled early</Tag>}
          <br />
        </span>
      ))}
      {Number(l.for_stock) > 0 && <span>{parts.length > 0 ? 'extra, to ' : ''}stock · {qty(l.for_stock)}</span>}
      {links.map((k) => (
        <span key={`${k.number}|${k.url}`}>
          {k.number}:{' '}
          <a href={k.url} target="_blank" rel="noopener noreferrer" title={k.url}>
            vendor page
          </a>
          <br />
        </span>
      ))}
    </small>
  );
}

function Totals({ o }: { o: OrderView }) {
  const draft = o.state === 'draft';
  const receipt = o.po.kind === 'receipt';
  const estShipping = shippingEstimate(o.vendor as ShippingRule | null, o.est_cents);
  const priced = o.lines.filter((l) => l.actual_cents !== null);
  const actualLines = priced.reduce((n, l) => n + (l.actual_cents ?? 0), 0);
  const shipping = o.invoices.reduce((n, i) => n + toCents(i.shipping ?? 0), 0);
  const tax = o.invoices.reduce((n, i) => n + toCents(i.tax ?? 0), 0);
  const billed = o.invoices.filter((i) => i.total !== null).reduce((n, i) => n + toCents(i.total), 0);
  const anyBilled = o.invoices.some((i) => i.total !== null);
  const allPriced = priced.length === o.lines.length && o.lines.length > 0;
  const actualTotal = actualLines + shipping + tax;
  const row = (label: ReactNode, value: ReactNode, strong?: boolean) => (
    <div className="ln">
      <span>{strong ? <b>{label}</b> : label}</span>
      <span className="right">{strong ? <b>{value}</b> : value}</span>
    </div>
  );
  return (
    <Panel
      label="What it comes to"
      right={
        <Info left>
          The estimate is each line at the estimating price it had when it went on the order, plus the vendor's usual shipping where one is set. The actual is what the invoice says: the unit prices typed on the lines, and the shipping and tax on the invoice.
        </Info>
      }
    >
      {row(receipt ? 'Lines, at the estimating price' : 'Lines, estimated', <Money cents={o.est_cents} />, receipt)}
      {!receipt && row('Shipping, estimated', estShipping === null ? <small>this vendor has no shipping rule</small> : <Money cents={estShipping} />)}
      {!receipt && row('Estimated total', <Money cents={o.est_cents + (estShipping ?? 0)} />, true)}
      {!draft && (
        <>
          {row(`Lines, actual${allPriced ? '' : ` (${priced.length} of ${o.lines.length} priced)`}`, priced.length ? <Money cents={actualLines} /> : '—')}
          {row('Shipping, actual', o.invoices.length ? <Money cents={shipping} /> : '—')}
          {row('Tax, actual', o.invoices.length ? <Money cents={tax} /> : '—')}
          {row('Actual total', allPriced ? <Money cents={actualTotal} /> : <small>once every line has its price</small>, true)}
          {anyBilled && allPriced && billed !== actualTotal && (
            <div className="warn">
              The invoices add up to {money(billed)}; the lines, shipping and tax here add up to {money(actualTotal)}. They differ by {money(Math.abs(billed - actualTotal))}.
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

function Invoices({ o, canBuy }: { o: OrderView; canBuy: boolean }) {
  const s = useSession();
  const users = useRows('user');
  const draft = o.state === 'draft';
  const prices = s.can('see_prices');
  if (!prices) return null;
  return (
    <Panel
      label={`Invoices and receipts (${o.invoices.length})`}
      right={
        <Info left>
          What the vendor billed: shipping, tax and the total, with the invoice or receipt itself attached. These, and the actual price on each line, are the fields receipt matching fills in. Until that is running they can be typed here. An order that arrives in two
          shipments can be billed twice, so there can be more than one.
        </Info>
      }
    >
      {draft && <small>Invoices come after the order is placed.</small>}
      {!draft && o.invoices.length === 0 && <Empty>Nothing entered yet.</Empty>}
      {o.invoices.map((i) => (
        <div key={i.id} className="ln" style={{ display: 'block' }}>
          <div className="form">
            <Field label="Invoice number">
              <Edit disabled={!canBuy} value={i.invoice_number ?? ''} onSave={(v) => save('purchase_invoice', i.id, { invoice_number: v || null })} />
            </Field>
            <Field label="Date">
              <Edit type="date" disabled={!canBuy} value={i.invoiced_on ?? ''} onSave={(v) => save('purchase_invoice', i.id, { invoiced_on: v || null })} />
            </Field>
            <Field label="Shipping">
              <Edit disabled={!canBuy} value={i.shipping === null ? '' : Number(i.shipping).toFixed(2)} parse={amount} onSave={(v) => save('purchase_invoice', i.id, { shipping: v === '' ? null : Number(v) })} />
            </Field>
            <Field label="Tax">
              <Edit disabled={!canBuy} value={i.tax === null ? '' : Number(i.tax).toFixed(2)} parse={amount} onSave={(v) => save('purchase_invoice', i.id, { tax: v === '' ? null : Number(v) })} />
            </Field>
            <Field label="Total billed">
              <Edit disabled={!canBuy} value={i.total === null ? '' : Number(i.total).toFixed(2)} parse={amount} onSave={(v) => save('purchase_invoice', i.id, { total: v === '' ? null : Number(v) })} />
            </Field>
          </div>
          <div className="row mid" style={{ justifyContent: 'space-between', marginTop: 6 }}>
            <InvoiceFile invoice={i} orderId={o.po.id} canBuy={canBuy} />
            <small>
              entered by {users.rows.find((u) => u.id === i.entered_by)?.name ?? 'someone'} · {shortDate(i.created_at)}{' '}
              {canBuy && (
                <button type="button" className="linklike" onClick={() => window.confirm('Remove this invoice?') && void deleteRow('purchase_invoice', i.id).then(refreshInventory)}>
                  Remove
                </button>
              )}
            </small>
          </div>
        </div>
      ))}
      {!draft && canBuy && (
        <div style={{ marginTop: 8 }}>
          <Btn small onClick={() => void insertRow('purchase_invoice', { purchase_order_id: o.po.id, invoiced_on: today(), entered_by: s.user?.id ?? null }).then(refreshInventory)}>
            Add an invoice or receipt
          </Btn>
        </div>
      )}
    </Panel>
  );
}

function InvoiceFile({ invoice, orderId, canBuy }: { invoice: Row; orderId: string; canBuy: boolean }) {
  const s = useSession();
  const file = useRows('file', { eq: { id: invoice.file_id }, enabled: !!invoice.file_id }).rows[0];
  const url = useSignedUrl(file?.storage_path);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const pick = async (list: FileList | null) => {
    const f = list?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const row = await uploadFile(f, 'purchase_order', orderId, s.user?.id ?? null);
      await save('purchase_invoice', invoice.id, { file_id: row.id });
    } finally {
      setBusy(false);
    }
  };
  return (
    <span>
      <input ref={input} type="file" accept="image/*,application/pdf" className="hide" onChange={(e) => void pick(e.target.files)} />
      {file ? (
        <a href={url ?? undefined} target="_blank" rel="noreferrer">
          {file.filename}
        </a>
      ) : (
        canBuy && (
          <Btn small disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Uploading…' : 'Attach the invoice'}
          </Btn>
        )
      )}
    </span>
  );
}

// ---------- the document that goes to the vendor ----------

/** The purchase order as a page to print or save as a PDF and send. */
export function PurchaseOrderDocument() {
  const { id } = useParams();
  const s = useSession();
  const names = useInventoryNames();
  const inv = useInventory();
  const o = inv.orders.find((x) => x.po.id === id);
  useCrumb([{ label: names.rail, to: '/inventory' }, { label: o?.po.number ?? 'Purchase order' }]);
  if (inv.loading) return <Loading />;
  if (!o) return <Empty>This order is no longer there.</Empty>;
  const company = s.setting<Record<string, string>>('company_info', {}) ?? {};
  const prices = s.can('see_prices');
  const cents = (l: OrderLineView) => (l.row.est_unit_price === null ? null : lineCents(l.row.qty, l.row.est_unit_price));
  return (
    <div>
      <div className="row mid noprint" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <span>
          <Link to="/inventory">← {names.purchasing}</Link>{' '}
          {o.state === 'draft' && <Tag kind="un">Draft: not placed yet</Tag>}{' '}
          {!company.name && <small>The heading is blank until "Company details on documents" is filled in under Settings.</small>}
        </span>
        <Btn read onClick={() => window.print()}>Print or save as PDF</Btn>
      </div>
      <div className="proposal">
        <div className="row" style={{ justifyContent: 'space-between', borderBottom: '2px solid var(--navy)', paddingBottom: 12 }}>
          <div>
            <h1>{company.name || ' '}</h1>
            <small>
              {[company.address, company.phone, company.email].filter(Boolean).map((x) => (
                <span key={x}>
                  {x}
                  <br />
                </span>
              ))}
            </small>
          </div>
          <div className="right">
            <h2>Purchase order</h2>
            <div className="big">{o.po.number}</div>
            <small>{longDate(o.po.placed_at ?? today())}</small>
          </div>
        </div>
        <div className="row" style={{ margin: '16px 0' }}>
          <div className="col">
            <small>To</small>
            <div>
              <b>{o.vendor?.name}</b>
            </div>
            <small>
              {[o.vendor?.email, o.vendor?.phone].filter(Boolean).join(' · ')}
            </small>
          </div>
          <div className="col">
            <small>Deliver to</small>
            <div>{company.name}</div>
            <small>{company.address}</small>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th className="right">Qty</th>
              <th>Unit</th>
              <th>Description</th>
              <th>Part number</th>
              {prices && <th className="right">Unit price</th>}
              {prices && <th className="right">Amount</th>}
            </tr>
          </thead>
          <tbody>
            {o.lines.map((l) => (
              <tr key={l.row.id}>
                <td className="right num">{qty(l.row.qty)}</td>
                <td>{l.material.unit ?? ''}</td>
                <td>
                  {l.material.description}
                  {requestLinks(l.requests).map((k) => (
                    <div key={`${k.number}|${k.url}`}>
                      <small>
                        {k.number}: {k.url}
                      </small>
                    </div>
                  ))}
                </td>
                <td>{l.material.part_number ?? ''}</td>
                {prices && <td className="right num">{l.row.est_unit_price === null ? '' : money(toCents(l.row.est_unit_price))}</td>}
                {prices && <td className="right num">{cents(l) === null ? '' : money(cents(l))}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {prices && (
          <div className="ln" style={{ marginTop: 8 }}>
            <span>
              <b>Expected total, before shipping and tax</b>
            </span>
            <span className="right big">{money(o.est_cents)}</span>
          </div>
        )}
        {o.po.note && <p style={{ marginTop: 14 }}>{o.po.note}</p>}
        <small style={{ display: 'block', marginTop: 18 }}>Please quote {o.po.number} on the invoice and the packing slip.</small>
      </div>
    </div>
  );
}
